import React, { ReactNode, useEffect, useState } from "react";
import { useTrans } from "@/utils/useTrans";
import { cn } from "@/libs/utils";
import { useRouter } from "next/router";
import CloseIcon from "@/v2-components/core/icon/close-icon";
import { useGameKeywordStore } from "@/store/games-recent-keywords-store";
import {
  useBhapticsContents,
  useNewGames,
  useSellableGames,
} from "@/hooks/useBhapticsContents";
import { BhapticsContent } from "@/store/reducers/contents";
import Image from "next/image";
import Link from "next/link";
import { useSearchKeywords } from "@/hooks/games/useSearchKeywords";
import { SearchPreviewLayoutWebView } from "@/components/webview/shared/SearchPreviewLayoutWebView";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/v2-components/ui/carousel";

export const GamesSearchPreview = ({
  onClose,
  className,
}: {
  onClose?: () => void;
  className?: string;
}) => {
  const { t } = useTrans(["games"]);
  const { data: trendingKeywords } = useSearchKeywords();
  const { games: newGames, isLoading: newGamesLoading } = useNewGames();
  const { data: sellableGames, isLoading: sellableGamesLoading } =
    useSellableGames();
  const { toggleGameKey } = useBhapticsContents();
  const { keywords, clearKeywords } = useGameKeywordStore();
  const [api, setApi] = useState<CarouselApi | null>(null);

  const isTrendingKeywords = trendingKeywords && trendingKeywords?.length > 0;
  const isViewRecent = keywords.length > 0;
  const isNewGames = newGames.length > 0;
  const isSellableGames = sellableGames.length > 0;

  return (
    <div className={cn("flex h-full flex-col", className)}>
      <section className="flex flex-1 flex-col gap-7 overflow-y-auto pb-4">
        {isViewRecent && (
          <SearchPreviewLayoutWebView
            className="px-4"
            header={
              <PreviewHeaderLayout
                title={t("Recent")}
                subTitle={
                  <button onClick={clearKeywords}>{t("Delete All")}</button>
                }
              />
            }
          >
            <RecentKeywords onSearch={onClose} />
          </SearchPreviewLayoutWebView>
        )}
        {isTrendingKeywords && (
          <SearchPreviewLayoutWebView
            className="px-4"
            header={t("Trending Keywords")}
          >
            <TrendingKeywords onSearch={onClose} keywords={trendingKeywords} />
          </SearchPreviewLayoutWebView>
        )}
        {isNewGames && (
          <SearchPreviewLayoutWebView
            className="md:px-4"
            header={
              <div
                className={
                  "relative flex items-center justify-between px-4 md:px-0"
                }
              >
                <h2>{t("New Games")}</h2>
                <Nav api={api} />
              </div>
            }
            isLoading={newGamesLoading}
          >
            <Games
              setApi={setApi}
              className="ml-2 mr-4 md:-ml-2 md:mr-0"
              games={newGames}
              onSearch={onClose}
            />
          </SearchPreviewLayoutWebView>
        )}
        {isSellableGames && (
          <SearchPreviewLayoutWebView
            className="md:px-4"
            header={
              <PreviewHeaderLayout
                className="px-4 md:px-0"
                title={t("Game Key Sales")}
                subTitle={
                  sellableGames.length > 4 && (
                    <button onClick={toggleGameKey}>{t("See all")}</button>
                  )
                }
              />
            }
            isLoading={sellableGamesLoading}
          >
            <Games
              className="ml-2 mr-4 md:-ml-2 md:mr-0"
              games={sellableGames.filter((_, i) => i < 4)}
              onSearch={onClose}
            />
          </SearchPreviewLayoutWebView>
        )}
      </section>
    </div>
  );
};
const TrendingKeywords = ({
  keywords,
  onSearch,
}: {
  keywords: string[];
  onSearch?: () => void;
}) => {
  const MAX_VISIBLE = 5;
  const router = useRouter();

  const handleClick = (keyword: string) => {
    router.replace(
      {
        pathname: "/games/search",
        query: {
          ...router.query,
          search: keyword,
        },
      },
      undefined,
    );
    onSearch?.();
  };

  return (
    <section>
      <ul className="flex max-w-full flex-wrap gap-3 text-sm font-400">
        {keywords.slice(0, MAX_VISIBLE).map((keyword) => (
          <li key={keyword}>
            <button
              type="button"
              onClick={() => handleClick(keyword)}
              className="whitespace-nowrap rounded-lg bg-coolgray-10 px-3 py-[10px]"
            >
              {keyword}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};

const RecentKeywords = ({ onSearch }: { onSearch?: () => void }) => {
  const MAX_VISIBLE = 5;

  const { keywords, removeKeyword } = useGameKeywordStore();
  const router = useRouter();

  const handleClick = (keyword: string) => {
    router.replace(
      {
        pathname: "/games/search",
        query: {
          ...router.query,
          search: keyword,
        },
      },
      undefined,
    );
    onSearch?.();
  };

  return (
    <section>
      <ul className="flex max-w-full flex-wrap gap-3 text-sm font-400">
        {keywords.slice(0, MAX_VISIBLE).map((keyword) => (
          <li key={keyword}>
            <button
              onClick={() => handleClick(keyword)}
              type="button"
              className="flex h-full gap-1 whitespace-nowrap rounded-lg border-[1px] border-coolgray-20 px-3 py-[10px]"
            >
              <p>{keyword}</p>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  removeKeyword(keyword);
                }}
              >
                <CloseIcon className="size-4" />
              </div>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
};

const Games = ({
  games,
  onSearch,
  className,
  setApi,
}: {
  games: BhapticsContent[];
  onSearch?: () => void;
  className?: string;
  setApi?: ((api: CarouselApi) => void) | undefined;
}) => {
  return (
    <section>
      <ByCarousel setApi={setApi} className={className}>
        {games.map((game, index) => {
          return (
            <CarouselItem key={index} className="basis-[29%] pl-2 md:basis-1/4">
              <Link
                key={game.id}
                href={`/games/experiences/${game.slug}`}
                onClick={onSearch}
              >
                <GameCard title={game.name} imageUrl={game.thumbnailLink} />
              </Link>
            </CarouselItem>
          );
        })}
      </ByCarousel>
    </section>
  );
};

const ByCarousel = ({
  children,
  className,
  setApi,
}: React.PropsWithChildren<{
  className?: string;
  setApi?: ((api: CarouselApi) => void) | undefined;
}>) => {
  return (
    <section>
      <Carousel opts={{ align: "start", slidesToScroll: 3 }} setApi={setApi}>
        <CarouselContent className={cn("-ml-2 flex", className)}>
          {children}
        </CarouselContent>
      </Carousel>
    </section>
  );
};

const Nav = ({ api }: { api: CarouselApi | null }) => {
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);

  useEffect(() => {
    if (!api) return;

    const updateScrollState = () => {
      setCanScrollNext(api.canScrollNext());
      setCanScrollPrev(api.canScrollPrev());
    };

    updateScrollState();

    api.on("select", updateScrollState);
    api.on("reInit", updateScrollState);

    return () => {
      api.off("select", updateScrollState);
      api.off("reInit", updateScrollState);
    };
  }, [api]);

  return (
    <div className="relative flex items-center gap-x-1">
      <NavButton
        className="rotate-180"
        onClick={(e) => {
          e.stopPropagation();
          api?.scrollPrev();
        }}
        disabled={!canScrollPrev}
      />
      <NavButton
        onClick={(e) => {
          e.stopPropagation();
          api?.scrollNext();
        }}
        disabled={!canScrollNext}
      />
    </div>
  );
};

const NavButton = ({
  disabled,
  className,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      {...rest}
      disabled={disabled}
      className={cn("block disabled:pointer-events-none", className)}
    >
      <NavIcon disabled={disabled} />
    </button>
  );
};

const NavIcon = ({
  className,
  disabled,
}: {
  className?: string;
  disabled?: boolean;
}) => {
  return (
    <svg
      className={cn(
        "size-5 text-coolgray-40 hover:text-coolgray-70 md:size-6",
        className,
      )}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g opacity={disabled ? 0.3 : 1}>
        <rect width="20" height="20" rx="10" fill="#F2F4F7" />
        <path
          d="M11.5798 9.99823L8.76118 12.9104C8.66545 13.0094 8.61643 13.1339 8.61413 13.2838C8.61194 13.4336 8.66095 13.5603 8.76118 13.664C8.86151 13.7676 8.98307 13.8193 9.12586 13.8193C9.26864 13.8193 9.3902 13.7676 9.49054 13.664L12.6005 10.4506C12.6652 10.3837 12.7109 10.3131 12.7375 10.2388C12.7642 10.1646 12.7775 10.0844 12.7775 9.99823C12.7775 9.91207 12.7642 9.83187 12.7375 9.75763C12.7109 9.68339 12.6652 9.61279 12.6005 9.54582L9.49054 6.33246C9.3947 6.23355 9.27423 6.1829 9.12914 6.18052C8.98417 6.17826 8.86151 6.2289 8.76118 6.33246C8.66095 6.43613 8.61084 6.56173 8.61084 6.70926C8.61084 6.85679 8.66095 6.98239 8.76118 7.08606L11.5798 9.99823Z"
          fill="currentColor"
        />
      </g>
    </svg>
  );
};

const PreviewHeaderLayout = ({
  title,
  subTitle,
  className,
}: {
  className?: string;
  title: ReactNode;
  subTitle: ReactNode;
}) => (
  <div className={cn("flex items-center justify-between", className)}>
    <h2 className="gap-4 text-lg font-500">{title}</h2>
    <p className="text-sm font-600 text-blue-60">{subTitle}</p>
  </div>
);

interface GameCardProps {
  imageUrl: string;
  title: string;
}

const GameCard: React.FC<GameCardProps> = ({ imageUrl, title }) => {
  return (
    <div className="flex w-full flex-col gap-y-[6px]">
      <div className="relative aspect-square w-full overflow-hidden rounded-[4px]">
        <Image src={imageUrl} alt={title} fill className="object-cover" />
      </div>
      <p className="line-clamp-2 overflow-hidden break-words text-sm font-600">
        {title}
      </p>
    </div>
  );
};
